import {
  FormBaseType,
  FormBasicsDto,
  FormQuestionDto,
  FormSectionDto,
  FormState,
} from "@green0meter/api-client";
import { create } from "zustand";
import yup, { object } from "yup";
import { FieldValues } from "react-hook-form";

export type ActiveInfo = {
  title: string;
  description?: string;
};

export type SearchFilter = "everything" | "answers" | "notes" | "fileNames";

export type FormSettings = {
  formBasics: FormBasicsDto;
  returnUrl?: string;
  isCsobForm?: boolean;
  button?: {
    label: string;
    onClick: (values: FieldValues, sections: CustomFormSectionDto[]) => void;
    disableSubmit?: boolean;
    popoverButtons?: {
      title: string;
      description: string;
      onClick: (values: FieldValues, sections: CustomFormSectionDto[]) => void;
      disableSubmit?: boolean;
    }[];
  };
  editMode?: boolean;
};

export type CustomFormQuestionDto = FormQuestionDto & {
  isVisible: boolean;
  showGetInTouchModal: boolean;
  questionIndex: number;
};

export type CustomFormSectionDto = Omit<FormSectionDto, "formQuestions"> & {
  formQuestions: CustomFormQuestionDto[];
  isVisible: boolean;
  isMatchingQuery: boolean;
  isRequired: boolean;
  isCompleted: boolean;
};

type MultistepFormStore = {
  formSettings: FormSettings;
  setFormSettings: (settings: FormSettings) => void;
  isSubmissionPending: boolean;
  setIsSubmissionPending: (isPending: boolean) => void;
  addQuestionOpen: boolean;
  setAddQuestionOpen: (open: boolean) => void;
  createQuestionOpen: boolean;
  setCreateQuestionOpen: (open: boolean) => void;
  sortQuestionsOpen: boolean;
  setSortQuestionsOpen: (open: boolean) => void;
  editSectionOpen: boolean;
  setEditSectionOpen: (open: boolean) => void;
  addSectionOpen: boolean;
  setAddSectionOpen: (open: boolean) => void;
  deleteSectionOpen: boolean;
  setDeleteSectionOpen: (open: boolean) => void;
  sections: CustomFormSectionDto[];
  setSections: (sections: CustomFormSectionDto[]) => void;
  filteredSections: CustomFormSectionDto[];
  setFilteredSections: (sections: CustomFormSectionDto[]) => void;
  activeSection: CustomFormSectionDto | undefined;
  setActiveSection: (section: CustomFormSectionDto | undefined) => void;
  nextSection: () => void;
  previousSection: () => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchFilter: SearchFilter;
  setSearchFilter: (filter: SearchFilter) => void;
  activeInfo: ActiveInfo | null;
  setActiveInfo: (info: ActiveInfo | null) => void;
  selectedPreviousSubmissions: string[];
  setSelectedPreviousSubmissions: (selectedPreviousSubmissions: string[]) => void;
  validationSchema: yup.ObjectSchema<Record<string, yup.AnySchema>>;
  setValidationSchema: (
    schema: yup.ObjectSchema<Record<string, yup.AnySchema>>,
  ) => void;
  resetStore: () => void;
};

const defaultFormSettings: FormSettings = {
  formBasics: {
    id: "",
    name: "",
    code: "",
    isTemplate: false,
    validFrom: "",
    validTo: "",
    isLocked: false,
    state: FormState.InProgress,
    formBaseType: FormBaseType.Custom,
    isCalculationReady: false,
  },
  returnUrl: "",
  isCsobForm: false,
  button: {
    label: "form.submit",
    onClick: (): void => {},
  },
};

export const useMultistepFormStore = create<MultistepFormStore>((set) => ({
  formSettings: defaultFormSettings,
  setFormSettings: (settings): void => set({ formSettings: settings }),
  isSubmissionPending: false,
  setIsSubmissionPending: (isPending): void =>
    set({ isSubmissionPending: isPending }),
  addQuestionOpen: false,
  setAddQuestionOpen: (open): void => set({ addQuestionOpen: open }),
  createQuestionOpen: false,
  setCreateQuestionOpen: (open): void => set({ createQuestionOpen: open }),
  sortQuestionsOpen: false,
  setSortQuestionsOpen: (open): void => set({ sortQuestionsOpen: open }),
  editSectionOpen: false,
  setEditSectionOpen: (open): void => set({ editSectionOpen: open }),
  addSectionOpen: false,
  setAddSectionOpen: (open): void => set({ addSectionOpen: open }),
  deleteSectionOpen: false,
  setDeleteSectionOpen: (open): void => set({ deleteSectionOpen: open }),
  sections: [],
  setSections: (sections): void => set({ sections: sections }),
  filteredSections: [],
  setFilteredSections: (sections): void => set({ filteredSections: sections }),
  activeSection: undefined,
  setActiveSection: (section): void => set({ activeSection: section }),
  nextSection: (): void =>
    set((state) => {
      const visibleSections = state.filteredSections.filter((s) => s.isVisible);
      const currentIndex = visibleSections.findIndex(
        (s) => s.id === state.activeSection?.id,
      );

      const nextIndex = (currentIndex + 1) % visibleSections.length;

      if (nextIndex === 0) {
        return {
          activeSection: state.activeSection,
        };
      }

      return {
        activeSection: visibleSections[nextIndex],
      };
    }),
  previousSection: (): void =>
    set((state) => {
      const visibleSections = state.filteredSections.filter((s) => s.isVisible);
      const currentIndex = visibleSections.findIndex(
        (s) => s.id === state.activeSection?.id,
      );

      const previousIndex =
        (currentIndex - 1 + visibleSections.length) % visibleSections.length;

      return {
        activeSection: visibleSections[previousIndex],
      };
    }),
  searchQuery: "",
  setSearchQuery: (query): void => set({ searchQuery: query }),
  searchFilter: "everything",
  setSearchFilter: (filter): void => set({ searchFilter: filter }),
  activeInfo: null,
  setActiveInfo: (info): void => set({ activeInfo: info }),
  selectedPreviousSubmissions: [],
  setSelectedPreviousSubmissions: (selectedPreviousSubmissions): void =>
    set({ selectedPreviousSubmissions: selectedPreviousSubmissions }),
  validationSchema: object({}),
  setValidationSchema: (schema): void => set({ validationSchema: schema }),
  resetStore: (): void => {
    set({
      formSettings: defaultFormSettings,
      isSubmissionPending: false,
      filteredSections: [],
      activeSection: undefined,
      searchQuery: "",
      searchFilter: "everything",
      activeInfo: null,
      selectedPreviousSubmissions: [],
      validationSchema: object({}),
    });
  },
}));
